import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';

/**
 * Simple connection monitoring service using browser's native online/offline events.
 * Replaces ng-connection-service which is incompatible with Angular 21+.
 */
@Injectable({
  providedIn: 'root'
})
export class ConnectionService implements OnDestroy {
  private isConnected$ = new BehaviorSubject<boolean>(navigator.onLine);
  private onlineHandler: () => void;
  private offlineHandler: () => void;

  constructor(private ngZone: NgZone) {
    this.initListeners();
  }

  ngOnDestroy(): void {
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
    }
    if (this.offlineHandler) {
      window.removeEventListener('offline', this.offlineHandler);
    }
  }

  private initListeners(): void {
    this.onlineHandler = () => {
      this.ngZone.run(() => {
        this.isConnected$.next(true);
      });
    };

    this.offlineHandler = () => {
      this.ngZone.run(() => {
        this.isConnected$.next(false);
      });
    };

    window.addEventListener('online', this.onlineHandler);
    window.addEventListener('offline', this.offlineHandler);
  }

  /**
   * Monitor connection status changes
   * @returns Observable that emits true when online, false when offline
   */
  monitor(): Observable<boolean> {
    return this.isConnected$.asObservable();
  }

  /**
   * Get current connection status
   */
  get isOnline(): boolean {
    return this.isConnected$.value;
  }
}
